import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Eye } from "lucide-react";
import { ReportType, ReportGenerationOptions } from "../types";
import { ReportingPeriod } from "@/types/ReportingPeriod";
import { ReportPreview } from "./ReportPreview";
import { ReportConfiguration } from "./ReportConfiguration";
import { ReportFormatOptions } from "./ReportFormatOptions";
import { ReportActions } from "./ReportActions";
import { ReportDataSummary } from "./ReportDataSummary";
import { ReportPeriodHeader } from "./ReportPeriodHeader";
import { useAtomValue } from "jotai";
import { SelectedFundAtom } from "@/store/FundStateAtom";
import { HoldingWithInstrumentAndAccount, TransactionWithInstrumentAndAccount } from "@/types";

interface ReportGeneratorProps {
  selectedReportingPeriod: ReportingPeriod | undefined;
  isGeneratingReport: boolean;
  isLoadingData: boolean;
  holdings: HoldingWithInstrumentAndAccount[];
  transactions: TransactionWithInstrumentAndAccount[];
  getReportPreviewData: () => any;
  onGenerateReport: (reportType: ReportType) => void;
}

export const ReportGenerator: React.FC<ReportGeneratorProps> = ({
  selectedReportingPeriod,
  isGeneratingReport,
  isLoadingData,
  holdings,
  transactions,
  getReportPreviewData,
  onGenerateReport,
}) => {
  const selectedFund = useAtomValue(SelectedFundAtom);
  const [reportOptions, setReportOptions] = useState<ReportGenerationOptions>({
    includeNAVSummary: true,
    includeHoldings: true,
    includePerformance: false,
    format: "PDF",
  });
  const [activeTab, setActiveTab] = useState<string>("configure");

  const handleOptionChange = (
    option: keyof ReportGenerationOptions,
    value: any
  ) => {
    setReportOptions((prev) => ({
      ...prev,
      [option]: value,
    }));
  };

  // Get preview data from controller
  const reportPreviewData = getReportPreviewData();

  return (
    <div className="space-y-6">
      <ReportPeriodHeader selectedReportingPeriod={selectedReportingPeriod} />
      
      {selectedReportingPeriod && (
        <>
          <ReportDataSummary
            holdings={holdings}
            transactions={transactions}
            isLoadingData={isLoadingData}
          />

          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Report Configuration</CardTitle>
              <CardDescription>
                Configure the content and format of your report
              </CardDescription>
            </CardHeader>

            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full max-w-md grid-cols-2">
                  <TabsTrigger value="configure">Configure</TabsTrigger>
                  <TabsTrigger value="preview" disabled={!reportPreviewData || isLoadingData}>
                    <Eye className="h-4 w-4 mr-1" />
                    Preview {isLoadingData && "(Loading...)"}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="configure" className="mt-6 space-y-6">
                  <ReportConfiguration
                    reportOptions={reportOptions}
                    onOptionChange={handleOptionChange}
                  />

                  {/* <ReportFormatOptions
                    reportOptions={reportOptions}
                    onOptionChange={handleOptionChange}
                  /> */}

                  <ReportActions
                    reportOptions={reportOptions}
                    isGeneratingReport={isGeneratingReport}
                    isLoadingData={isLoadingData}
                    canPreview={!!reportPreviewData}
                    onPreview={() => setActiveTab("preview")}
                    onGenerateReport={onGenerateReport}
                  />
                </TabsContent>

                <TabsContent value="preview" className="mt-6">
                  {reportPreviewData && (
                    <ReportPreview
                      reportData={reportPreviewData}
                      reportOptions={reportOptions}
                      holdings={holdings}
                      transactions={transactions}
                    />
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
